import { UseFormGetValues } from "react-hook-form";

export function atLeastOneOfGroup(groupNames: string[], message: string) {
  return (formValues: Record<string, any>) => {
    return groupNames.some(name => !!formValues[name]) || message;
  };
}

/**
 * Validates that the input contains only supported characters for document generation.
 * Supports printable ASCII (32-126), extended Windows-1252 characters (128–255),
 * tabs, newlines, and smart/curly quotes (U+2018, U+2019, U+201C, U+201D).
 *
 * @param value - The input value to validate
 * @returns true if valid, error message string if invalid
 */
export function isDocGenSupportedCharacters(value: string): true | string {
  if (!value) return true; // Allow empty values (use 'required' validation separately)

  // RegEx for printable ASCII (32-126) and extended Windows-1252 characters (128–255)
  const TAB = '\x09';
  const NEW_LINE = '\x0A';
  const ASCII = '\x20-\x7E';
  const WIN_1252 = '\x80-\xFF';
  // Smart/curly quotes from iOS and macOS auto-correct
  const SMART_QUOTES = '\u2018\u2019\u201C\u201D';

  const VALID_CHARS_REGEX = new RegExp(
    `^[${TAB}${NEW_LINE}${ASCII}${WIN_1252}${SMART_QUOTES}]*$`
  );

  if (VALID_CHARS_REGEX.test(value)) {
    return true;
  }

  // Find all invalid characters
  const SMART_QUOTE_CODES = [0x2018, 0x2019, 0x201C, 0x201D];
  const invalidChars = new Set<string>();
  for (const char of value) {
    const code = char.charCodeAt(0);
    // Check if character is NOT in the valid ranges
    if (code !== 0x09 && code !== 0x0A && !(code >= 0x20 && code <= 0x7E) && !(code >= 0x80 && code <= 0xFF) && !SMART_QUOTE_CODES.includes(code)) {
      invalidChars.add(char);
    }
  }

  if (invalidChars.size > 0) {
    const charList = Array.from(invalidChars).map(c => `"${c}"`).join(', ');
    return `Invalid characters have been used. Please edit the response so the following characters have been removed: ${charList}`;
  }
  
  return 'Invalid character';
}

export const mapValidations = (
  validations: any[],
  getValues?: UseFormGetValues<any>,
  atLeastOneOf?: string,
  atLeastOneOfMessage?: string
) => {
  if (!validations || !Array.isArray(validations) || validations.length === 0) return {};

  const result: any = {};

  validations.forEach((v: any) => {
      // Merge multiple validate functions if needed
      const validateFns: Array<(value: any) => true | string> = [];

      if (atLeastOneOf && getValues) {
        validateFns.push((value: any) => {
          const otherValue = getValues(atLeastOneOf);
          return value || otherValue ? true : atLeastOneOfMessage || "At least one field is required";
        });
      }

      // Reject whitespace-only values for all text fields
      validateFns.push((value: any) => {
        if (typeof value === 'string' && value.length > 0 && value.trim().length === 0) {
          return "This field cannot contain only spaces";
        }
        return true;
      });

      if (v.required) {
        result.required = v.required;
      }
      if (v.pattern && v.pattern.value && v.pattern.message) {
        result.pattern = {
          value: new RegExp(v.pattern.value),
          message: v.pattern.message,
        };
      }
      if (v.minLength) result.minLength = v.minLength;
      if (v.maxLength) result.maxLength = v.maxLength;

      // Custom dependency validation
      if (v.custom && getValues) {
        validateFns.push((value: any) => {
          const isChecked = getValues(v.custom.dependsOn);
          if (isChecked && value < v.custom.minValue) {
            return v.custom.message;
          }
          return true;
        });
      }

      // Date range validation: maxDaysAfter
      // Validates that this date is within X days after another date field
      // Usage in JSON: { "maxDaysAfter": { "field": "start_date", "days": 14, "message": "Must be within 14 days" } }
      if (v.maxDaysAfter && getValues) {
        validateFns.push((value: any) => {
          if (!value) return true; // Allow empty (use 'required' separately)
          const referenceDate = getValues(v.maxDaysAfter.field);
          if (!referenceDate) return true; // If reference field is empty, skip validation

          const currentDate = new Date(value);
          const refDate = new Date(referenceDate);
          const maxDays = v.maxDaysAfter.days || 14;

          // Calculate the maximum allowed date
          const maxDate = new Date(refDate);
          maxDate.setDate(maxDate.getDate() + maxDays);

          if (currentDate > maxDate) {
            return v.maxDaysAfter.message || `Date must be within ${maxDays} days of the reference date`;
          }
          return true;
        });
      }

      // Document generation supported characters validation
      // Check if 'type' field is set to 'isDocGenSupportedCharacters'
      if (v.type === 'isDocGenSupportedCharacters') {
        validateFns.push((value: any) => {
          if (!value) return true; // Allow empty (use 'required' separately)
          const validationResult = isDocGenSupportedCharacters(value);
          // Use custom message if provided, otherwise use default
          return validationResult ? validationResult : true;
        });
      }

      // If any validate functions were added, combine them
      if (validateFns.length > 0) {
        result.validate = (value: any) => {
          for (const fn of validateFns) {
            const res = fn(value);
            if (res !== true) return res;
          }
          return true;
        };
      }
  });

  return result;
};